import React, { useState } from "react";
import { Button, Radio } from "antd";
import PackageChoice from "./components/packageChoice/PackageChoice";
import EnterpriseOption from "../../../components/Billing/EnterpriseOption";
import { ENTERPRISE_PACKET, isEnterprisePacket } from "../../../components/Billing/enterprise";
import { ANNUAL_BILLING_TERMS, formatPrice, getPlanPrice } from "./planPricing";
import {
   getRegisteringPlan,
   isBilledYearly,
   saveRegisteringPlan,
   setBilledYearly
} from "./registrationSession";

// The package step, the first of the sign up: the plan picked here is what
// decides the level the account is set up for — see accountLevel.js — and the
// profile step after it is what creates the account (Registration.jsx).
//
// Enterprise is picked here too, with no price: its prices and limits are
// agreed with our team afterwards. The review step asks for it and says how to
// tell us what is needed (RegisterStep3, components/Billing/enterprise.js).

function RegisterStep2({
   onConfirmPlan,
   onBack,
   backLabel,
   organizationLevel,
   // Whether the plans come with the organization's one free trial. A sign up
   // always has it; an account coming back to pay has used it.
   offersTrial = true
}) {
   // Both are read back from the registration rather than started fresh, so a
   // reload, a restored tab and the review step's own way back all find the
   // plan that was picked still picked and the billing period still where it
   // was put.
   const [selectedPlan, setSelectedPlan] = useState(getRegisteringPlan);
   const [isYearly, setIsYearly] = useState(isBilledYearly);

   // An organization that is already registered arrives here with the level it
   // registered under, and is offered every plan in one row (PackageChoice). A
   // sign up has no level yet and is shown both tracks to pick one.
   const hasRegisteredLevel = typeof organizationLevel === "number";

   const handlePricingToggle = (e) => {
      setIsYearly(e.target.value === "yearly");
      setBilledYearly(e.target.value === "yearly");
   };

   // Kept the moment it is picked, not only on the way out of the step: what is
   // stored is what a reload comes back to, and a selection that only lands
   // when Continue is pressed is a selection a reload loses.
   const selectPlan = (plan) => {
      setSelectedPlan(plan);
      saveRegisteringPlan(plan);
   };

   const confirmPlan = () => {
      setBilledYearly(isYearly);
      saveRegisteringPlan(selectedPlan);
      onConfirmPlan(selectedPlan);
   };

   return (
      <>
         {/* Only where something precedes this step. First in the sign up flow
             it has nothing to go back to, and the header already offers log in. */}
         {onBack && (
            <div
               className="register-back-link"
               style={{ marginBottom: "40px", cursor: "pointer" }}
               onClick={onBack}
            >
               <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
               >
                  <path
                     d="M14.7358 8.2339L2.73584 8.2339M2.73584 8.2339L8.10255 2.86719M2.73584 8.2339L8.10255 13.6006"
                     stroke="#5770CB"
                     strokeLinecap="round"
                     strokeLinejoin="round"
                  />
               </svg>{" "}
               {backLabel}
            </div>
         )}

         <div className="package-choice">
            <h1 className="heading-select-plan">
               {hasRegisteredLevel ? "Select your plan" : "Choose your package"}
            </h1>
            <p className="package-choice__subtitle">
               {hasRegisteredLevel
                  ? "The plans your account is set up for. Pick the one you'd like to continue on."
                  : `Two kinds of account, side by side. The plan you pick is the one your account is set up for${
                       offersTrial ? ", and each one starts on a free trial" : ""
                    }.`}
            </p>

            <div className="package-choice__billing">
               <Radio.Group onChange={handlePricingToggle} value={isYearly ? "yearly" : "monthly"}>
                  <Radio value="yearly">Annual plan</Radio>
                  <Radio value="monthly">Monthly plan</Radio>
               </Radio.Group>
            </div>

            <PackageChoice
               isYearly={isYearly}
               selectedPlanId={selectedPlan?.id}
               onSelect={selectPlan}
               organizationLevel={organizationLevel}
               offersTrial={offersTrial}
               enterpriseOption={
                  <EnterpriseOption
                     terms={null}
                     isRequested={false}
                     isYearly={isYearly}
                     isSelected={isEnterprisePacket(selectedPlan?.id)}
                     onSelect={() => selectPlan(ENTERPRISE_PACKET)}
                     requestNote="Prices and limits agreed with our team for your organization. Create your account, then tell us what you need on the review step. Nothing is charged."
                  />
               }
            />

            {/* An annual plan is charged each month, so there is no yearly
                total to add up: what it commits to is said instead. Enterprise
                has no price until our team agrees one. */}
            {isYearly && selectedPlan && !isEnterprisePacket(selectedPlan.id) && (
               <p
                  style={{ textAlign: "center", marginTop: "24px" }}
                  className="paragraph-total-year"
               >
                  {/* One child: the box is a flex row, which would space the parts apart. */}
                  <span>
                     {selectedPlan.name}, annual plan:{" "}
                     <b>USD {formatPrice(getPlanPrice(selectedPlan, true))}/month</b>,{" "}
                     {ANNUAL_BILLING_TERMS}
                  </span>
               </p>
            )}

            {/* No promo code here: it is asked for on the review step, next to
                the price it changes and the button that sends it (RegisterStep3). */}
            <div
               className="register-button-group package-choice__continue"
               style={{
                  marginTop: "24px",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center"
               }}
            >
               <Button disabled={!selectedPlan} onClick={confirmPlan} className="kapSurveyPrimaryButton">
                  Continue
               </Button>
            </div>
            {!selectedPlan && <p className="package-choice__continue-hint">Pick a plan to continue.</p>}
         </div>
      </>
   );
}

export default RegisterStep2;
